import {
  Callout,
  FormField,
  RadioGroup,
  TextInput,
  RadioBox,
} from '@dev-spendesk/grapes';
import { type FormikProps } from 'formik';
import React, { useId } from 'react';

import type { CostCenter } from 'modules/budgets/models/costCenter';
import { useFeature } from 'src/core/common/hooks/useFeature';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';

import { type FormValues } from './formValues';
import { MultipleCostCenterAutocomplete } from '../../../cost-centers/components/MultipleCostCenterAutocomplete';

type Props = {
  costCenters: CostCenter[];
} & Pick<
  FormikProps<FormValues>,
  'values' | 'errors' | 'setFieldValue' | 'handleChange' | 'handleSubmit'
>;

export const EcFormModalContent = ({
  costCenters,
  // formik props
  values,
  errors,
  setFieldValue,
  handleChange,
  handleSubmit,
}: Props) => {
  const { t } = useTranslation('global');
  const radioGroupId = useId();
  const isCostCentersFeatureEnabled = useFeature(
    FEATURES.COST_CENTERS_ACTIVATED,
  );

  return (
    <form id="ec-form" onSubmit={handleSubmit}>
      <FormField
        className="mb-24"
        label={t('expenseCategories.form.name')}
        alertMessage={errors.name}
      >
        <TextInput
          name="name"
          placeholder={t('expenseCategories.form.namePlaceholder')}
          value={values.name}
          onChange={handleChange}
        />
      </FormField>
      {isCostCentersFeatureEnabled && (
        <>
          <div className="mb-8 text-left">
            <FormField
              label={t('expenseCategories.form.costCenterAssignationFieldName')}
              description={t(
                'expenseCategories.form.costCenterAssignationFieldDescription',
              )}
            >
              <RadioGroup
                aria-labelledby={radioGroupId}
                name="costCenterAssignationType"
                value={values.costCenterAssignationType}
                onChange={(e) => {
                  setFieldValue('costCenterAssignationType', e.target.value);
                }}
              >
                <RadioBox
                  value="all"
                  label={t('expenseCategories.form.costCenterAssignation.all')}
                />
                <RadioBox
                  value="some"
                  label={t('expenseCategories.form.costCenterAssignation.some')}
                />
              </RadioGroup>
            </FormField>
          </div>
          {values.costCenterAssignationType === 'some' && (
            <>
              <FormField
                className="text-left"
                label={t('misc.costCenter')}
                visuallyHideLabel
                alertMessage={
                  typeof errors.costCenterIds === 'string'
                    ? errors.costCenterIds
                    : undefined
                }
              >
                <MultipleCostCenterAutocomplete
                  costCenters={costCenters}
                  selectedCostCenterIds={values.costCenterIds}
                  onSelect={(options) => {
                    setFieldValue(
                      'costCenterIds',
                      options.map((option) => option.key),
                    );
                  }}
                />
              </FormField>
              <Callout
                className="mt-8"
                title={t('expenseCategories.form.onlyRequestors')}
                variant="info"
              />
            </>
          )}
        </>
      )}
    </form>
  );
};
